import React from "react";
import {
  defineMessages,
  IntlShape,
  FormattedMessage as T,
  useIntl,
} from "react-intl";
import { makeStyles, Theme, darken } from "@material-ui/core/styles";
import FormHelperText from "@material-ui/core/FormHelperText";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import Dialog from "@material-ui/core/Dialog";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import ClearIcon from "@material-ui/icons/Clear";
import { useForm, Controller, ErrorMessage } from "react-hook-form";
import * as yup from "yup";
import moment from "moment";

import { ControlFormValues } from "../ControlForm";
import { LocationControlFormValues } from "../LocationControl";
import {
  ControlReview,
  EmitNfpsBody,
  NfpsExemptionReason,
  NfpsSignificantRight,
  Ticket,
  FpsCountry,
} from "@egis-smartcity/parking";
import FullscreenNotification, {
  FullscreenNotificationProps,
} from "../../FullscreenNotification";
import useControlReview from "../../../hooks/useControlReview";
import useCityConfig from "../../../hooks/useCityConfig";
import useAuth from "../../../hooks/useAuth";

import MobilityIcon from "../../../assets/icons/nfpsReason/mobilityIcon.svg";
import MobilityIconEmpty from "../../../assets/icons/nfpsReason/mobilityIconEmpty.svg";
import PresenceIcon from "../../../assets/icons/nfpsReason/presenceIcon.svg";
import PresenceIconEmpty from "../../../assets/icons/nfpsReason/presenceIconEmpty.svg";
import AggressionIcon from "../../../assets/icons/nfpsReason/aggressionIcon.svg";
import AggressionIconEmpty from "../../../assets/icons/nfpsReason/aggressionIconEmpty.svg";
import TypoIcon from "../../../assets/icons/nfpsReason/errorIcon.svg";
import TypoIconEmpty from "../../../assets/icons/nfpsReason/errorIconEmpty.svg";
import AuthorizationIcon from "../../../assets/icons/nfpsReason/authorizationIcon.svg";
import AuthorizationIconEmpty from "../../../assets/icons/nfpsReason/authorizationIconEmpty.svg";
import DiplomatIcon from "../../../assets/icons/nfpsReason/diplomatIcon.svg";
import DiplomatIconEmpty from "../../../assets/icons/nfpsReason/diplomatIconEmpty.svg";
import TroublesomeIcon from "../../../assets/icons/nfpsReason/troublesomeIcon.svg";
import TroublesomeIconEmpty from "../../../assets/icons/nfpsReason/troublesomeIconEmpty.svg";
import RoadworkIcon from "../../../assets/icons/nfpsReason/roadworkIcon.svg";
import RoadworkIconEmpty from "../../../assets/icons/nfpsReason/roadworkIconEmpty.svg";
import GraceIcon from "../../../assets/icons/nfpsReason/graceIcon.svg";
import GraceIconEmpty from "../../../assets/icons/nfpsReason/graceIconEmpty.svg";
import FreeIcon from "../../../assets/icons/nfpsReason/otherIcon.svg";
import FreeIconEmpty from "../../../assets/icons/nfpsReason/otherIconEmpty.svg";
import UnexploitableIcon from "../../../assets/icons/nfpsReason/unexploitableIcon.svg";
import UnexploitableIconEmpty from "../../../assets/icons/nfpsReason/unexploitableIconEmpty.svg";
import AuthorizedIcon from "../../../assets/icons/nfpsReason/authorizedIcon.svg";
import AuthorizedIconEmpty from "../../../assets/icons/nfpsReason/authorizedIconEmpty.svg";
import NoPaymentRequiredIcon from "../../../assets/icons/nfpsReason/noPaymentRequiredIcon.svg";
import NoPaymentRequiredIconEmpty from "../../../assets/icons/nfpsReason/noPaymentRequiredIconEmpty.svg";
import OtherIcon from "../../../assets/icons/nfpsReason/otherIcon.svg";
import OtherIconEmpty from "../../../assets/icons/nfpsReason/otherIconEmpty.svg";
import ForeignPlateIcon from "../../../assets/icons/nfpsReason/foreignPlateIcon.svg";
import ForeignPlateIconEmpty from "../../../assets/icons/nfpsReason/foreignPlateIconEmpty.svg";
import OffsiteAutobusIcon from "../../../assets/icons/nfpsReason/offsiteAutobusIcon.svg";
import OffsiteAutobusIconEmpty from "../../../assets/icons/nfpsReason/offsiteAutobusIconEmpty.svg";
import TechnicalIcon from "../../../assets/icons/nfpsReason/technicalIcon.svg";
import TechnicalIconEmpty from "../../../assets/icons/nfpsReason/technicalIconEmpty.svg";
import LightIcon from "../../../assets/icons/nfpsReason/lightIcon.svg";
import LightIconEmpty from "../../../assets/icons/nfpsReason/lightIconEmpty.svg";
import PedaIcon from "../../../assets/icons/nfpsReason/pedaIcon.svg";
import PedaIconEmpty from "../../../assets/icons/nfpsReason/pedaIconEmpty.svg";
import Tooltip from "@material-ui/core/Tooltip";
import { DeviceServiceChannel, createDeviceServiceChannel } from "../../../lib";
import { version } from "../../../../package.json";
import { NfpsExemptionReasonLabelValue } from "@egis-smartcity/parking";

const ReasonIcons = {
  not_a_bus_parking_space: {
    full: OffsiteAutobusIcon,
    empty: OffsiteAutobusIconEmpty,
  },
  foreign: {
    full: ForeignPlateIcon,
    empty: ForeignPlateIconEmpty,
  },
  tech: {
    full: TechnicalIcon,
    empty: TechnicalIconEmpty,
  },
  mobility: {
    full: MobilityIcon,
    empty: MobilityIconEmpty,
  },
  presence: {
    full: PresenceIcon,
    empty: PresenceIconEmpty,
  },
  aggression: {
    full: AggressionIcon,
    empty: AggressionIconEmpty,
  },
  typo: {
    full: TypoIcon,
    empty: TypoIconEmpty,
  },
  authorization: {
    full: AuthorizationIcon,
    empty: AuthorizationIconEmpty,
  },
  diplomat: {
    full: DiplomatIcon,
    empty: DiplomatIconEmpty,
  },
  troublesome: {
    full: TroublesomeIcon,
    empty: TroublesomeIconEmpty,
  },
  roadwork: {
    full: RoadworkIcon,
    empty: RoadworkIconEmpty,
  },
  grace: {
    full: GraceIcon,
    empty: GraceIconEmpty,
  },
  user_at_the_parking_meter: {
    full: OtherIcon,
    empty: OtherIconEmpty,
  },
  free: {
    full: FreeIcon,
    empty: FreeIconEmpty,
  },
  no_marking: {
    full: OtherIcon,
    empty: OtherIconEmpty,
  },
  out_of_order: {
    full: OtherIcon,
    empty: OtherIconEmpty,
  },
  unexploitable: {
    full: UnexploitableIcon,
    empty: UnexploitableIconEmpty,
  },
  unusable: {
    full: UnexploitableIcon,
    empty: UnexploitableIconEmpty,
  },
  authorized: {
    full: AuthorizedIcon,
    empty: AuthorizedIconEmpty,
  },
  emergency: {
    full: AuthorizedIcon,
    empty: AuthorizedIconEmpty,
  },
  no_payment_required: {
    full: NoPaymentRequiredIcon,
    empty: NoPaymentRequiredIconEmpty,
  },
  not_paying: {
    full: NoPaymentRequiredIcon,
    empty: NoPaymentRequiredIconEmpty,
  },
  negative_fps: {
    full: TroublesomeIcon,
    empty: TroublesomeIconEmpty,
  },
  other: {
    full: OtherIcon,
    empty: OtherIconEmpty,
  },
  error: {
    full: TroublesomeIcon,
    empty: TroublesomeIconEmpty,
  },
  peda: {
    full: PedaIcon,
    empty: PedaIconEmpty,
  },
  light: {
    full: LightIcon,
    empty: LightIconEmpty,
  },
};

const useStyles = makeStyles((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    // Use !important to override the Modal inline-style.
    zIndex: `${theme.zIndex.appBar - 1} !important` as unknown as number,
  },
  dialogTitle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.common.white,
    backgroundColor: "#014f57",
    padding: "8px 16px",
    "&: first-child": {
      display: "flex",
      flexDirection: "column",
    },
  },
  closeIcon: {
    position: "absolute",
    top: 0,
    right: 0,
  },
  dialogContent: {
    minWidth: 300,
    display: "flex",
    flexWrap: "wrap",
    backgroundColor: theme.palette.common.white,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    padding: "10px 16px",
    "& >:nth-child(odd)": {
      paddingRight: "8px",
    },
  },
  infoContainer: {
    width: "50%",
    minHeight: "40px",
    "& >:first-child": {
      height: "100%",
      width: "100%",
      display: "flex",
      alignItems: "center",
      color: theme.palette.grey[700],
      borderBottom: "1px solid #111111",
      "& >*": {
        flex: 1,
        margin: 0,
        textAlign: "left",
      },
      "& >:nth-child(2)": {
        fontWeight: 600,
      },
    },
  },
  reasonContainer: {
    padding: "16px 8px 8px 8px",
    "& > *": {
      maxHeight: 350,
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      overflow: "auto",
    },
    "& > * > *": {
      width: "20%",
      minWidth: "100px",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "space-between",
      "& >:nth-child(2)": {
        flex: 1,
        justifyContent: "space-between",
      },
    },
  },
  reasonIcon: {
    margin: "8px 0",
  },
  radioContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: theme.palette.grey[800],
    "& >*": {
      flex: 1,
      padding: "4px 8px",
      margin: 0,
      textAlign: "left",
    },
    "& >:first-child": {
      textTransform: "uppercase",
    },
    "& >:nth-child(2)": {
      display: "flex",
      justifyContent: "flex-end",
    },
  },
  radioColor: {
    color: "#134f57",
  },
  formControl: {
    width: "100%",
    marginBottom: theme.spacing(2.5),
  },
  labelContainer: {
    width: "80%",
  },
  labelReason: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  dialogActions: {
    height: "60px",
    display: "flex",
    alignItems: "stretch",
    backgroundColor: "white",
    padding: "8px 16px",
    "& > *": {
      flex: 1,
    },
  },
  paperDialog: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "#134f57",
  },
  commentField: {
    width: "100%",
    padding: "0 8px",
  },
  errorMessage: {
    color: "red",
    padding: "0 8px",
    margin: 0,
    maxWidth: 300,
  },
  loader: {
    color: "#134f57",
  },
  confirmButton: {
    backgroundColor: "#134f57",
    color: theme.palette.common.white,
    marginLeft: 4,
    "&:hover": {
      backgroundColor: darken("#134f57", 0.1),
    },
  },
  cancelButton: {
    color: theme.palette.common.white,
    backgroundColor: "#6c6c6c",
    marginRight: 4,
    "&:hover": {
      backgroundColor: darken("#6c6c6c", 0.1),
    },
  },
}));

const msg = defineMessages({
  nfpsDialogTitle: {
    id: "nfpsDialog.nfpsDialogTitle.label",
    description: "Label of the modal title.",
    defaultMessage: "Emit an NFPS",
  },
  nfpsNotifSuccess: {
    id: "nfpsDialog.nfpsNotifSuccess.message",
    description: "Message of the success notification.",
    defaultMessage: "Action emitted successfully.",
  },
  nfpsNotifError: {
    id: "nfpsDialog.nfpsNotifError.message",
    description: "Message of the error notification.",
    defaultMessage:
      "Cannot issue the action. Please check the information entered",
  },
  emitNfpsFailureNotif: {
    id: "nfpsDialog.emitNfpsFailureNotif.message",
    description: "Message of the emit NFPS failure notification.",
    defaultMessage:
      "Enable to send an NFPS, you will receive a new control in a few seconds.",
  },
  successTitle: {
    id: "nfpsDialog.successTitle.label",
    description: "Label of the success title notification.",
    defaultMessage: "Success",
  },
  nfpsLicencePlate: {
    id: "nfpsDialog.nfpsLicencePlate.label",
    description: "Label of the licence plate.",
    defaultMessage: "Licence plate",
  },
  controlDate: {
    id: "nfpsDialog.controlDate.label",
    description: "Label of the date.",
    defaultMessage: "Date",
  },
  nfpsReasonSubTitle: {
    id: "nfpsDialog.nfpsReasonSubTitle.label",
    description: "Label of subtitle reason.",
    defaultMessage: "Reason",
  },
  nfpsOtherField: {
    id: "nfpsDialog.nfpsOtherField.label",
    description: "Label of the comment field when other reason is checked.",
    defaultMessage: "Comment",
  },
  confirmButton: {
    id: "nfpsDialog.confirmButton.label",
    description: "Label of the confirm button.",
    defaultMessage: "Emit NFPS",
  },
  cancelButton: {
    id: "nfpsDialog.cancelButton.label",
    description: "Label of the cancel button.",
    defaultMessage: "Cancel",
  },
  reasonErrorMessage: {
    id: "nfpsDialog.reasonErrorMessage.message",
    description: "error message when no reason checked.",
    defaultMessage: "Please, select a reason.",
  },
  reasonRequired: {
    id: "nfpsDialog.reason.required",
    description: "Message displayed when the reason field is empty.",
    defaultMessage: "The reason is required",
  },
});

function getReasonFormSchema(
  y: typeof yup,
  nfpsExemptionReasons: NfpsExemptionReasonLabelValue[],
  t: IntlShape["formatMessage"]
) {
  return y.object().shape({
    reason: y
      .mixed<NfpsExemptionReason>()
      .oneOf(
        nfpsExemptionReasons.map((reason) => {
          return reason.value;
        }),
        t(msg.reasonErrorMessage)
      )
      .required(t(msg.reasonRequired)),
    comment: y.string(),
  });
}

export type ReasonFormValues = yup.InferType<
  ReturnType<typeof getReasonFormSchema>
>;

export type NfpsDialogProps = {
  /**
   * State of the dialog.
   */
  open: boolean;
  /**
   * Data of the current control.
   */
  controlReview: ControlReview;
  /**
   * Data of the current control.
   */
  controlForm: ControlFormValues;
  /**
   * The location control form values.
   */
  locationControlFormValues: LocationControlFormValues;
  /**
   * Tickets of the current control.
   */
  tickets: Ticket[];
  /**
   * URL of the plate image.
   */
  plateImage: string;
  /**
   * URL of the selected images.
   */
  imageSelection: string[];
  /**
   * Catch event when close button is clicked.
   */
  onCloseDialog(dialog: "nfps"): void;
  /**
   * Handle when an NFPS is succesfully send.
   */
  onNfpsSent(force?: number): void;
};

/**
 * Dialog form to emit an NFPS.
 *
 * @param props - The NfpsDialog component props.
 */
function NfpsDialog(props: NfpsDialogProps) {
  ///////////////// PROPS, HOOKS AND STATE SETUP////////////////////////////////

  const {
    open,
    controlReview,
    controlForm,
    locationControlFormValues,
    plateImage,
    imageSelection,
    onCloseDialog,
    onNfpsSent,
    tickets,
  } = props;
  const { formatMessage: t } = useIntl();
  const { cityConfig, sectors, zones } = useCityConfig();
  const nfpsExemptionReasons = React.useMemo(() => {
    return cityConfig?.review?.nfps?.items || [];
  }, [cityConfig]);
  const {
    control,
    errors,
    formState,
    handleSubmit,
    setValue,
    getValues,
    watch,
  } = useForm<ReasonFormValues>({
    defaultValues: {
      reason: undefined,
      comment: "",
    },
    validationSchema: getReasonFormSchema(yup, nfpsExemptionReasons, t),
  });
  const { controlReviewService } = useControlReview();
  const { cityId } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [errorCount, setErrorCount] = React.useState(0);
  const [notification, setNotification] =
    React.useState<FullscreenNotificationProps>({ open: false });
  const disabled = React.useMemo(
    () => formState.isSubmitting || loading,
    [formState.isSubmitting, loading]
  );

  const reset = React.useCallback(() => {
    // setValue("reason", "");
    setValue("comment", "");
    setLoading(false);
    setErrorCount(0);
    setNotification({ open: false });
  }, [setValue]);

  ///////////////// CSS ////////////////////////////////////////////////////////

  const classes = useStyles();

  ///////////////// INIT AND SIDE EFFECT(S) ////////////////////////////////////

  const reason = watch("reason"); // Needed to display the comment field when `reason === "other"`.

  // If the errorCount is greater than 2 -> request a new control.
  React.useEffect(() => {
    if (errorCount === 2) {
      setErrorCount(42); // Just to be sure we don't run this effect anymore.
      const timeoutId = setTimeout(() => {
        reset();
        onNfpsSent(1);
      }, 3000);
      setNotification({
        open: true,
        message: t(msg.emitNfpsFailureNotif),
        type: "error",
        onCloseButtonClick: (event: React.SyntheticEvent<Element, Event>) => {
          clearTimeout(timeoutId);
          reset();
          onNfpsSent(1);
        },
      });
      onCloseDialog("nfps");
    }
  }, [errorCount, onCloseDialog, onNfpsSent, reset, t]);

  // Extract the NFPS significant rights from the tickets array.
  const nfpsSignificantRights: NfpsSignificantRight[] = React.useMemo(() => {
    if (!cityId) return [];

    return tickets.map((ticket) => {
      const nfpsSignificantRight: NfpsSignificantRight = {
        type: "TICKET",
        cityId,
        startDatetime: ticket.startDateTime,
        endDatetime: ticket.endDateTime,
        parkId: ticket.locationCode,
        rightPrice: ticket.amountPaid,
        zoneId: ticket.tariffCode,
      };

      return nfpsSignificantRight;
    });
  }, [cityId, tickets]);

  ///////////////// WORKFLOW ///////////////////////////////////////////////////

  const handleClose = React.useCallback(() => {
    reset();
    onCloseDialog("nfps");
  }, [onCloseDialog, reset]);

  const onSubmit = React.useCallback(
    (data: ReasonFormValues, _event?: React.BaseSyntheticEvent) => {
      if (
        !controlReviewService ||
        !controlReview.control ||
        !controlReview.controlId
      )
        return;

      const currentZone = zones?.find(
        (zone) => zone.code === locationControlFormValues.zone.value
      );
      const currentSector = sectors?.find(
        (sector) => sector.code === locationControlFormValues.sector.value
      );

      const nfps: EmitNfpsBody & {
        deviceServiceChannel?: DeviceServiceChannel;
      } = {
        matricule: controlReview.agentId,
        date: controlReview.control.date,
        type: "NFPS",
        params: {
          exemptionReason: data.reason as NfpsExemptionReason,
          comment: data.reason === "other" ? (data.comment as string) : "",
          vehicleCategory:
            controlForm.vehicleType.value === null
              ? undefined
              : controlForm.vehicleType.value,
          zoneId: currentZone?.zoneId,
          parkId: currentSector?.parkId,
          licensePlate: controlForm.licensePlate,
          parkingRightHistory: nfpsSignificantRights,
          plateCountry: controlForm.country.value as FpsCountry,
          vehicleBrand: controlForm.carBrand.value,
          vehicleModel: controlForm.carModel?.value,
        },
        deviceServiceChannel:
          //@ts-ignore
          cityConfig?.review?.fps?.siret === "21750001600019"
            ? createDeviceServiceChannel({
                deviceType: "AUTOMATED_CONTROL_VEHICLE",
                operatingSystem: "IMAGE_REVIEW",
                application: {
                  author: {
                    organizationId: cityConfig?.fps?.worksFor?.organizationId,
                    name: cityConfig?.fps?.worksFor?.name,
                    url: cityConfig?.fps?.worksFor?.url,
                    address: {
                      ...cityConfig?.fps?.worksFor?.address,
                    },
                  },
                  name: "PARIS",
                  softwareVersion: version || "unknown version",
                },
              })
            : undefined,
      };

      if (
        locationControlFormValues.lat !== null &&
        locationControlFormValues.lon !== null
      ) {
        nfps.position = {
          lat: locationControlFormValues.lat,
          lon: locationControlFormValues.lon,
          acc: controlReview.control.gps_pdop,
        };
      }

      setLoading(true);
      controlReviewService
        .emitNfps(controlReview.controlId, {
          nfpsData: nfps,
          mediasUrl: [plateImage, ...imageSelection],
        })
        .then((nfps) => {
          const timeoutId = setTimeout(() => {
            reset();
            onNfpsSent(1);
          }, 3000);
          setNotification({
            open: true,
            message: t(msg.nfpsNotifSuccess),
            type: "success",
            onCloseButtonClick: (
              event: React.SyntheticEvent<Element, Event>
            ) => {
              clearTimeout(timeoutId);
              reset();
              onNfpsSent(1);
            },
          });
          onCloseDialog("nfps");
        })
        .catch((error) => {
          if (process.env.NODE_ENV !== "production") console.error(error);
          setLoading(false);
          const timeoutId = setTimeout(() => {
            setNotification({ open: false });
            setErrorCount((prev) => ++prev);
          }, 3000);
          setNotification({
            open: true,
            message: t(msg.nfpsNotifError),
            type: "error",
            onCloseButtonClick: (
              event: React.SyntheticEvent<Element, Event>
            ) => {
              clearTimeout(timeoutId);
              setNotification({ open: false });
              setErrorCount((prev) => ++prev);
            },
          });
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cityConfig,
      controlForm.licensePlate,
      controlReview.agentId,
      controlReview.control,
      controlReview.controlId,
      controlReviewService,
      imageSelection,
      locationControlFormValues.lat,
      locationControlFormValues.lon,
      locationControlFormValues.sector.value,
      locationControlFormValues.zone.value,
      nfpsSignificantRights,
      onCloseDialog,
      onNfpsSent,
      plateImage,
      reset,
      sectors,
      t,
      zones,
    ]
  );

  const getIconReason = (reason: NfpsExemptionReason) => {
    if (
      ReasonIcons[reason] &&
      ReasonIcons[reason].full &&
      ReasonIcons[reason].empty
    ) {
      return getValues("reason") === reason
        ? ReasonIcons[reason].full
        : ReasonIcons[reason].empty;
    } else {
      return getValues("reason") === reason
        ? ReasonIcons["error"].full
        : ReasonIcons["error"].empty;
    }
  };

  return (
    <>
      <Dialog
        className={classes.root}
        classes={{ paper: classes.paperDialog }}
        onClose={handleClose}
        open={open}
        disableBackdropClick
        disableEscapeKeyDown
        disablePortal
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.dialogTitle}>
            <Typography variant="h6">
              <T {...msg.nfpsDialogTitle} />
            </Typography>
            {controlReview.control?.date !== undefined && (
              <>
                <Typography variant="subtitle1">
                  <small>
                    {moment(controlReview.control.date).format("L")}
                  </small>
                </Typography>
                <Typography variant="subtitle2">
                  <b>{moment(controlReview.control.date).format("LTS")}</b>
                </Typography>
              </>
            )}
            <IconButton
              className={classes.closeIcon}
              color="inherit"
              disabled={disabled}
              onClick={handleClose}
            >
              <ClearIcon fontSize="large" />
            </IconButton>
          </div>
          <div className={classes.dialogContent}>
            <div className={classes.reasonContainer}>
              <Controller
                name="reason"
                as={
                  <RadioGroup aria-label={t(msg.nfpsReasonSubTitle)}>
                    {nfpsExemptionReasons.map((reason) => (
                      <div key={reason.value}>
                        <FormControlLabel
                          key={reason.value}
                          label={
                            <>
                              <img
                                className={classes.reasonIcon}
                                src={getIconReason(reason.value)}
                                alt={reason.label}
                              />
                              <Tooltip title={reason.label}>
                                <Typography className={classes.labelReason}>
                                  {reason.label}
                                </Typography>
                              </Tooltip>
                            </>
                          }
                          classes={{
                            root: classes.formControl,
                            label: classes.labelContainer,
                          }}
                          labelPlacement="top"
                          value={reason.value}
                          disabled={disabled}
                          control={<Radio color="primary" />}
                        />
                      </div>
                    ))}
                  </RadioGroup>
                }
                control={control}
              />
            </div>
            <ErrorMessage name="reason" errors={errors}>
              {({ message }) => (
                <FormHelperText error>{message}</FormHelperText>
              )}
            </ErrorMessage>
            <Collapse in={reason === "other"} className={classes.commentField}>
              <Controller
                name="comment"
                as={
                  <TextField
                    label={t(msg.nfpsOtherField)}
                    disabled={disabled}
                    required={errors.comment?.type === "required"}
                    error={!!errors.comment}
                    helperText={!!errors.comment && errors.comment.message}
                    fullWidth
                    multiline
                    rowsMax="2"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                }
                control={control}
              />
            </Collapse>
          </div>
          <div className={classes.dialogActions}>
            <Button
              className={classes.cancelButton}
              size="small"
              variant="contained"
              disabled={disabled}
              onClick={handleClose}
            >
              <T {...msg.cancelButton} />
            </Button>
            <Button
              className={classes.confirmButton}
              variant="contained"
              type="submit"
              startIcon={
                (formState.isSubmitting || loading) && (
                  <CircularProgress className={classes.loader} size={20} />
                )
              }
              disabled={disabled || reason === undefined}
            >
              <T {...msg.confirmButton} />
            </Button>
          </div>
        </form>
      </Dialog>
      <FullscreenNotification {...notification} />
    </>
  );
}

export default NfpsDialog;
